import React, { useEffect, useRef, useState } from 'react';
import {
  Bell,
  Laptop2,
  DoorClosed,
  DoorOpen,
  Cake,
  UserCog,
  ArrowLeftRight,
  BookOpen,
  Stethoscope,
  Building2,
  Settings,
  Volume2,
  Volume2 as Volume2Off,
  X,
  Play,
  Check,
} from 'lucide-react';
import type {
  Request,
  CheckInRequest,
  Student,
  Item,
  Summons,
  HallPass,
  HallPassType,
} from '../../types';

// Extended HallPass interface for transformed data
interface TransformedHallPass extends Omit<HallPass, 'hallPassId' | 'hallPassTypeId' | 'durationMinutes'> {
  id: string;
  type: string;
  duration: number;
  status: string;
  rawStatus: string;
  requestedBy?: { name: string; avatar: string };
  location?: string;
}
import {
  //playNotificationSound,
  previewSound,
  NotificationType,
  availableSounds,
  SoundId,
  updateSoundMapping,
  getCurrentSound,
} from '../../utils/audio';
import { startNotificationConnection } from '../../services/notificationHubService';
import { useHallPassTypes } from '../../contexts/HallPassTypesContext';
import { UserContext } from '../../contexts/UserContext';
import axios from "../../axios-config";
import logo from '../../assets/logo-black.svg';
import PageView from './PageView';  // make sure PageView is in this folder
import { HallPassStatuses } from '../../types/enums';
import { createPortal } from 'react-dom';     // NEW

// extended to include hall passes that are pending return processing
export type HallPassStatus =
  | 'pending'
  | 'pending-return'   // awaiting staff review after student has returned
  | 'returned';

// desktop‑notification helpers -------------------------------
const NOTIF_SUPPORTED = typeof window !== 'undefined' && 'Notification' in window;

/** Request permission on first component mount */
function useRequestNotificationPermission() {
  useEffect(() => {
    if (NOTIF_SUPPORTED && Notification.permission === 'default') {
      Notification.requestPermission().catch(() => { /* ignored */ });
    }
  }, []);
}

/** One‑liner wrapper (safe‑guards permission & support) */
export function pushDesktopNotification(title: string, opts?: NotificationOptions) {
  if (NOTIF_SUPPORTED && Notification.permission === 'granted') {
    const optionsWithLogo = { ...opts, icon: logo };
    // eslint‑disable‑next‑line no‑new
    new Notification(title, optionsWithLogo);
  }
}
// ----------------------------------------------------------------

/**
 * A single React component that can render either as a full‑page notification center
 * **or** as a bell‑icon dropdown, depending on the `mode` prop.
 *
 * ```tsx
 * // Full page
 * <Notifications mode="page" {...props} />
 *
 * // Bell dropdown
 * <Notifications mode="bell" {...props} />
 * ````

 */
export type NotificationsMode = 'page' | 'bell';

export interface NotificationsProps {
  mode: NotificationsMode;
  /** equipment checkout requests */
  requests: Request[];
  /** equipment return requests */
  checkInRequests: CheckInRequest[];
  /** optional hall‑pass requests (only used in bell mode) */
  hallPasses?: HallPass[];
  students: Student[];
  items: Item[];
  /** navigate to a top‑level notifications page (bell‑mode only) */
  onNavigateToPage?: () => void;
  onApproveRequest: (request: Request) => void;
  onDenyRequest: (request: Request) => void;
  onApproveCheckIn: (request: CheckInRequest) => void;
  onDenyCheckIn: (request: CheckInRequest) => void;
  onClearAllRequests: () => void;
  onClearAllCheckIns: () => void;
  /** initial hall passes from user profile */
  initialHallPasses?: any[];
}

/* ------------------------------------------------------------------------- */
/*                              Helper hooks                                 */
/* ------------------------------------------------------------------------- */

/** Returns birthdays that match *today* (local time) */
function useTodaysBirthdays(students: Student[]) {
  return students.filter((s) => {
    if (!s.birthDate) return false;
    var dateStr = s.birthDate;
    const parts = dateStr.split('T')[0].split('-');
    const m = Number(parts[1]) - 1;
    const d = Number(parts[2]);
    const now = new Date();
    return now.getMonth() === m && now.getDate() === d;
  });
}

function getHallPassIcon(type: HallPassType) {
  switch (type as unknown as string) {
    case 'library':
      return <BookOpen className="w-4 h-4 text-slate-500" />;
    case 'health':
      return <Stethoscope className="w-4 h-4 text-slate-500" />;
    case 'office':
      return <Building2 className="w-4 h-4 text-slate-500" />;
    default:
      return <DoorClosed className="w-4 h-4 text-slate-500" />;
  }
}

/* ------------------------------------------------------------------------- */
/*                             Settings modal                                */
/* ------------------------------------------------------------------------- */

interface NotificationSettings {
  equipment: boolean;
  equipmentSound: boolean;
  returns: boolean;
  returnsSound: boolean;
  hallPasses: boolean;
  hallPassesSound: boolean;
  birthdays: boolean;
  birthdaysSound: boolean;
  summons: boolean;
  summonsSound: boolean;
  scheduleEnabled: boolean;
  scheduleStart: string;
  scheduleEnd: string;
  scheduleDays: string[];
}

const defaultSettings: NotificationSettings = {
  equipment: false,
  equipmentSound: true,
  returns: true,
  returnsSound: true,
  hallPasses: true,
  hallPassesSound: true,
  birthdays: true,
  birthdaysSound: true,
  summons: true,
  summonsSound: true,
  scheduleEnabled: false,
  scheduleStart: '08:00',
  scheduleEnd: '15:00',
  scheduleDays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
};

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
  settings: NotificationSettings;
  setSettings: React.Dispatch<React.SetStateAction<NotificationSettings>>;
}

function SettingsModal({ open, onClose, settings, setSettings }: SettingsModalProps) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full space-y-6">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">Notification Settings</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 rounded-lg p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        {/* --- schedule ---------------------------------------------------- */}
        <div className="space-y-4">
          <div className="border-b border-gray-200 pb-4">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-medium text-gray-900">School Hours</h4>
              <button
                onClick={() =>
                  setSettings((s) => ({ ...s, scheduleEnabled: !s.scheduleEnabled }))
                }
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.scheduleEnabled ? 'bg-primary-600' : 'bg-gray-200'
                  }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.scheduleEnabled ? 'translate-x-6' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
            {settings.scheduleEnabled && (
              <div className="space-y-3">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <label className="block text-sm text-gray-600 mb-1">Start Time</label>
                    <input
                      type="time"
                      value={settings.scheduleStart}
                      onChange={(e) =>
                        setSettings((s) => ({ ...s, scheduleStart: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm text-gray-600 mb-1">End Time</label>
                    <input
                      type="time"
                      value={settings.scheduleEnd}
                      onChange={(e) =>
                        setSettings((s) => ({ ...s, scheduleEnd: e.target.value }))
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm text-gray-600 mb-2">School Days</label>
                  <div className="flex flex-wrap gap-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(
                      (day) => (
                        <button
                          key={day}
                          onClick={() =>
                            setSettings((s) => ({
                              ...s,
                              scheduleDays: s.scheduleDays.includes(day)
                                ? s.scheduleDays.filter((d) => d !== day)
                                : [...s.scheduleDays, day],
                            }))
                          }
                          className={`px-3 py-1 text-sm rounded-lg transition-colors ${settings.scheduleDays.includes(day)
                            ? 'bg-primary-100 text-primary-700 font-medium'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                        >
                          {day.slice(0, 3)}
                        </button>
                      ),
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          {/* --- individual toggles -------------------------------------- */}
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Notify me about:</h4>
          ={[

              { key: 'equipment', label: 'Equipment Requests', icon: Laptop2 },
              { key: 'returns', label: 'Equipment Returns', icon: ArrowLeftRight },
              { key: 'hallPasses', label: 'Hall Passes', icon: DoorClosed },
              { key: 'birthdays', label: 'Birthdays', icon: Cake },
              { key: 'summons', label: 'Student Summons', icon: UserCog },
            ].map(({ key, label, icon: Icon }) => {
              const soundKey = `${key}Sound` as keyof NotificationSettings;
              return (
                <div
                  key={key}
                  className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-5 h-5 text-gray-500" />
                    <span className="text-gray-900">{label}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() =>
                        setSettings((s) => ({ ...s, [key]: !s[key as keyof NotificationSettings] }))
                      }
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings[key as keyof NotificationSettings] ? 'bg-primary-600' : 'bg-gray-200'
                        }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings[key as keyof NotificationSettings] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                      />
                    </button>
                    <button
                      onClick={() =>
                        setSettings((s) => ({ ...s, [soundKey]: !s[soundKey] }))
                      }
                      className="p-1.5 rounded-lg hover:bg-gray-100"
                      title={`${settings[soundKey] ? 'Disable' : 'Enable'} sound`}
                    >
                      {settings[soundKey] ? (
                        <Volume2 className="w-5 h-5 text-primary-600" />
                      ) : (
                        <Volume2Off className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                    {settings[soundKey] && (
                      <div className="flex items-center gap-2">
                        <select
                          value={getCurrentSound(key as NotificationType)}
                          onChange={(e) => {
                            updateSoundMapping(key as NotificationType, e.target.value as SoundId);
                            setSettings((s) => ({ ...s }));
                          }}
                          className="text-sm border border-gray-200 rounded-lg px-2 py-1"
                        >
                          {Object.keys(availableSounds).map((id) => (
                            <option key={id} value={id}>
                              {id
                                .split('-')
                                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                                .join(' ')}
                            </option>
                          ))}
                        </select>
                        <button
                          onClick={() => previewSound(getCurrentSound(key as NotificationType))}
                          className="p-1.5 rounded-lg hover:bg-gray-100"
                          title="Preview sound"
                        >
                          <Play className="w-4 h-4 text-gray-400 hover:text-primary-600" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/*                           Main component body                             */
/* ------------------------------------------------------------------------- */

function mapStatus(id: number): HallPassStatus {
  switch (id) {
    case 4:
      return 'pending';
    case 5:
      return 'pending-return';
    case 6:
      return 'returned';
    case 7:
      return 'returned';
    default:
      return 'returned';
  }
}

export const Notifications = ({
  mode = 'bell',
  requests = [],               // <-- correct destructuring (no type annotation here)
  checkInRequests = [],
  hallPasses = [],
  students = [],
  items = [],
  ...props
}) => {
  const {
    mode: propMode,
    onNavigateToPage,
    onApproveRequest,
    onDenyRequest,
    onApproveCheckIn,
    onDenyCheckIn,
    onClearAllRequests,
    onClearAllCheckIns,
    initialHallPasses,
  } = props;

  const { userProfile } = React.useContext(UserContext);
  const [hallPassesState, setHallPasses] = React.useState<TransformedHallPass[]>([]);

  const { hallPassTypes } = useHallPassTypes();

  // Request notification permission
  useRequestNotificationPermission();

  /* ---------------- settings state ----------------------- */
  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings);
  const [showSettings, setShowSettings] = useState(false);

  /* ---------------- new state for late return modal -------------- */
  const [lateReturnModalOpen, setLateReturnModalOpen] = useState(false);
  const [selectedLateReturnId, setSelectedLateReturnId] = useState<string | null>(null);

  React.useEffect(() => {
    const source = userProfile?.hallpasses || initialHallPasses;
    if (source && hallPassTypes.length > 0) {
      const transformed = source.map(transformIncomingHallPass);
      setHallPasses(transformed);
    }
  }, [userProfile, initialHallPasses, hallPassTypes]);
  /** Normalises the server payload into a HallPass while keeping local extra status values. */
  function transformIncomingHallPass(p: any): TransformedHallPass {
    const type = hallPassTypes.find(
        (ht) => ht.hallPassTypeId === (p.hallPassTypeId ?? p.type?.hallPassTypeId)
    );

    // --- Summons detection: prioritize status === 'summons' ---
    let rawStatus: string;
    if (p.status === 'summons') {
        rawStatus = 'summons';
    } else if (p.summonedBy) {
        rawStatus = 'summons';
    } else {
        rawStatus = mapStatus(p.hallPassStatusId);
        // if (p.hallPassStatusId === HallPassStatuses.Returned && !p.returnDecisionedAt) {
        //     rawStatus = 'pending-returned';
        // }
    }

    // 1) figure out which field actually holds the teacher’s userId
    const requestingTeacherId =
      typeof p.summonedBy   === 'string' ? p.summonedBy   :
      typeof p.SummonedBy   === 'string' ? p.SummonedBy   :
      typeof p.teacherId    === 'string' ? p.teacherId    :
      typeof p.TeacherId    === 'string' ? p.TeacherId    :
      typeof p.createdBy    === 'string' ? p.createdBy    :
      typeof p.CreatedBy    === 'string' ? p.CreatedBy    :
      undefined;

    // 2) look that up in your profile
    const teacher = requestingTeacherId
      ? userProfile?.teachers?.find(t => t.id === requestingTeacherId)
      : undefined;

    const requestedBy = teacher
      ? { name: teacher.name, avatar: teacher.avatar }
      : undefined;    return {
      ...p,
      status: rawStatus,
      id: p.hallPassId?.toString?.() || p.id?.toString?.() || 'temp',
      studentId: p.studentId,
      type: type?.hallPassTypeDescription || p.type || 'Unknown',
      rawStatus,
      duration: type?.hallPassTypeDuration || p.duration || 0,
      notes: p.notes || '',
      createdAt: p.createdAt ? new Date(p.createdAt) : new Date(),
      requestDecisionedAt: p.requestDecisionedAt ? new Date(p.requestDecisionedAt) : null,
      returnDecisionedAt:  p.returnDecisionedAt  ? new Date(p.returnDecisionedAt)  : null,
      requestedBy,
      summonedBy: p.summonedBy ?? p.SummonedBy ?? null,
      location: p.summonedTo ?? p.SummonedTo,
    } satisfies TransformedHallPass;
  }

  React.useEffect(() => {
    if (hallPassTypes.length > 0) {
      startNotificationConnection((newPass: any) => {
        // Normalize status (SignalR sometimes sends capitalized e.g. "Returned")
        const normalizedStatus = (newPass.status || '').toString().toLowerCase();
        const passId = newPass.hallPassId?.toString?.() || newPass.id?.toString?.();

        // Dispatch global event for other components (map, active hall passes) to listen
        window.dispatchEvent(new CustomEvent('hallPassUpdated', { 
          detail: { 
            type: normalizedStatus === 'returned' || normalizedStatus === 'cancelled' || normalizedStatus === 'expired' ? 'remove' : 'upsert',
            hallPass: { ...newPass, status: normalizedStatus }
          } 
        }));

        // Any terminal status removes the pass from pending lists
        if (["approved","denied","returned","cancelled","expired"].includes(normalizedStatus)) {
          setHallPasses(prev => prev.filter(p => p.id !== passId));
          return;
        }

        // Pending return handling
        if (normalizedStatus === 'pending-return' || newPass.hallPassStatusId === HallPassStatuses.Returned) {
          const transformed = transformIncomingHallPass({ ...newPass, status: 'returned' });
          setHallPasses(prev => {
            const filtered = prev.filter(p => p.id !== transformed.id);
            return [...filtered, transformed];
          });
          return;
        }

        // Default: add/update request or summons
        const transformed = transformIncomingHallPass({ ...newPass, status: normalizedStatus || newPass.status });
        setHallPasses(prev => {
          const filtered = prev.filter(p => p.id !== transformed.id);
          return [...filtered, transformed];
        });
      }, "nonStudent");
    }
  }, [hallPassTypes]);

  /* ---------------- derived lists ------------------------------------ */
  // equipment checkout / return data come from the dedicated props
  const pendingRequests   = requests.filter((r: any)      => r.status === 'pending');
  const pendingCheckIns   = checkInRequests.filter((r: any) => r.status === 'pending');
  
  // hall-pass related lists use the internal hallPassesState
  const pendingHallPasses = hallPassesState.filter((p: any) => p.status === 'pending'  && !p.summonedBy);
  const pendingReturns    = hallPassesState.filter((p: any) => p.status === 'returned');
  const summonsList       = hallPassesState.filter((p: any) => p.status === 'summons' && p.requestDecisionedAt === null);
  
  // birthdays unaffected
  const todaysBirthdays   = useTodaysBirthdays(students);

  /* ---------------- hall‑pass handlers ------------------------------- */
  function handleApproveHallPass(id: string) {
    axios.post('/api/HallPasses/ApproveHallPasses', [id], {
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => {
        setHallPasses(prev => prev.filter(p => p.id !== id));
      })
      .catch(error => {
        console.error("Approve hall pass error:", error);
      });
  }
  function handleDenyHallPass(id: string) {
    axios.post('/api/HallPasses/DenyHallPasses', [id], {
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => {
        setHallPasses(prev => prev.filter(p => p.id !== id));
      })
      .catch(error => {
        console.error("Deny hall pass error:", error);
      });
  }
  function processReturnOnTime(id: string) {
    axios.post('/api/hallpasses/ProcessAllPassReturns', [id], {
      headers: { 'Content-Type': 'application/json' },
    })
      .then(() =>
        setHallPasses((prev) => prev.filter((p) => p.id !== id)),
      )
      .catch((e) => console.error('Process on‑time return error:', e));
  }
  function processReturnLate(id: string, lateByMinutes: number) {
    axios.post(`/api/hallpasses/ProcessAllPassReturns?LateByMinutes=${lateByMinutes}`, [id], {
      headers: { 'Content-Type': 'application/json' },
    })
      .then(() =>
        setHallPasses((prev) => prev.filter((p) => p.id !== id)),
      )
      .catch((e) => console.error('Process late return error:', e));
  }
  function handleLateReturnClick(id: string) {
    setSelectedLateReturnId(id);
    setLateReturnModalOpen(true);
  }
  function handleLateReturnSubmit(lateMinutes: number) {
    if (selectedLateReturnId) {
      processReturnLate(selectedLateReturnId, lateMinutes);
    }
    setLateReturnModalOpen(false);
    setSelectedLateReturnId(null);
  }

  /* ---------------- summons handlers ------------------------------- */
  function handleAcceptSummons(id: string) {
    axios.post('/api/HallPasses/AcceptSummons', [id], {
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => {
        setHallPasses(prev => prev.filter(p => p.id !== id));
      })
      .catch(error => {
        console.error("Accept summons error:", error);
      });
  }
  function handleAcceptAllSummons() {
    const ids = summonsList.map(s => s.id);
    axios.post('/api/HallPasses/AcceptSummons', ids, {
      headers: { 'Content-Type': 'application/json' }
    })
      .then(() => {
        setHallPasses(prev => prev.filter(p => !ids.includes(p.id)));
      })
      .catch(error => {
        console.error("Accept all summons error:", error);
      });
  }

  /* ---------------- notification sound & desktop notice ------- */
  const prevCounts = useRef({
    req: pendingRequests.length,
    ret: pendingCheckIns.length,
    hall: pendingHallPasses.length,
    returns: pendingReturns.length,
    //birthdays: todaysBirthdays.length,
    summons: summonsList.length,
  });

  useEffect(() => {
    const pc = prevCounts.current;
    const newCounts = {
      req: pendingRequests.length,
      ret: pendingCheckIns.length,
      hall: pendingHallPasses.length,
      returns: pendingReturns.length,
      //birthdays: todaysBirthdays.length,
      summons: summonsList.length,
    };

    const maybeNotify = (
      prev: number,
      curr: number,
      title: string,
      body: string,
      tag?: string
    ) => {
      if (curr > prev) {
        pushDesktopNotification(title, { body, tag });
      }
    };

    if (settings.equipment) {
      maybeNotify(pc.req, newCounts.req, 'New Equipment Request', `${newCounts.req} pending`, 'equipment');
    }
    if (settings.returns) {
      maybeNotify(pc.ret, newCounts.ret, 'New Equipment Return', `${newCounts.ret} pending`, 'returns');
    }
    if (settings.hallPasses) {
      maybeNotify(pc.hall, newCounts.hall, 'New Hall Pass Request', `${newCounts.hall} pending`, 'hallPasses');
      maybeNotify(pc.returns, newCounts.returns, 'Hall Pass Return', `${newCounts.returns} to review`, 'hallPasses');
    }
    // if (settings.birthdays) {
    //   maybeNotify(pc.birthdays, newCounts.birthdays, 'Today’s Birthdays', `${newCounts.birthdays} student(s)`, 'birthdays');
    // }
    if (settings.summons) {
      maybeNotify(pc.summons, newCounts.summons, 'Student Summons', `${newCounts.summons} active`, 'summons');
    }

    prevCounts.current = newCounts;
  }, [
    pendingRequests.length,
    pendingCheckIns.length,
    pendingHallPasses.length,
    pendingReturns.length,
    //todaysBirthdays.length,
    summonsList.length,
  ]);

  /* ---------------- page vs bell render ------------------ */
  if (propMode === 'page') {
    return (
      <>
        <PageView
          pendingRequests={pendingRequests}
          pendingCheckIns={pendingCheckIns}
          summons={summonsList as unknown as Summons[]}
          todaysBirthdays={todaysBirthdays}
          students={students}
          items={items}
          activeTabOverride={undefined}
          setActiveTabOverride={undefined}
          onApproveRequest={onApproveRequest}
          onDenyRequest={onDenyRequest}
          onApproveCheckIn={onApproveCheckIn}
          onDenyCheckIn={onDenyCheckIn}
          onClearAllRequests={onClearAllRequests}
          onClearAllCheckIns={onClearAllCheckIns}
          onOpenSettings={() => setShowSettings(true)}
          pendingHallPasses={pendingHallPasses}
        />
        <SettingsModal
          open={showSettings}
          onClose={() => setShowSettings(false)}
          settings={settings}
          setSettings={setSettings}
        />
      </>
    );
  }

  return (
    <>
      <BellView
        pendingRequests={pendingRequests}
        pendingCheckIns={pendingCheckIns}
        pendingHallPasses={pendingHallPasses}
        pendingReturns={pendingReturns}
        summons={summonsList as unknown as Summons[]}
        todaysBirthdays={todaysBirthdays}
        students={students}
        items={items}
        onApproveRequest={onApproveRequest}
        onDenyRequest={onDenyRequest}
        onApproveCheckIn={onApproveCheckIn}
        onDenyCheckIn={onDenyCheckIn}
        onClearAllRequests={onClearAllRequests}
        onClearAllCheckIns={onClearAllCheckIns}
        onNavigateToPage={onNavigateToPage}
        onOpenSettings={() => setShowSettings(true)}
        onApproveHallPass={handleApproveHallPass}
        onDenyHallPass={handleDenyHallPass}
        onProcessReturnOnTime={processReturnOnTime}
        onLateReturnClick={handleLateReturnClick}
        onAcceptSummons={handleAcceptSummons}
        onAcceptAllSummons={handleAcceptAllSummons}
      />
      <SettingsModal
        open={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        setSettings={setSettings}
      />
      {lateReturnModalOpen && (
        <LateReturnModal
          open={lateReturnModalOpen}
          onClose={() => {
            // just hide the dialog – keep the pass active
            setLateReturnModalOpen(false);
          }}
          onSubmit={handleLateReturnSubmit}
        />
      )}
    </>
  );
};

function LateReturnModal({ open, onClose, onSubmit }: { open: boolean; onClose: () => void; onSubmit: (lateMinutes: number) => void }) {
  const [lateMinutes, setLateMinutes] = useState<number>(0);
  if (!open) return null;

  /* render into document.body to avoid transformed ancestors */
  const modal = (
    <div className="fixed inset-0 bg-black/50 z-50 grid place-items-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-sm w-full space-y-4">
        {/* --- content stays unchanged --- */}
        <h3 className="text-lg font-semibold text-gray-900">Late Return</h3>
        <p className="text-sm text-gray-600 text-center">
          Enter how many minutes late the student is:
        </p>
        <input
          type="number"
          value={lateMinutes}
          onChange={(e) => setLateMinutes(Number(e.target.value))}
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
        />
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-3 py-1 text-sm text-gray-600 hover:bg-gray-100 rounded"
          >
            Cancel
          </button>
          <button
            onClick={() => onSubmit(lateMinutes)}
            className="px-3 py-1 text-sm text-white bg-error-600 hover:bg-error-700 rounded"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);  // ← portal
}

interface BellViewProps {
  pendingRequests: any;
  pendingCheckIns: CheckInRequest[];
  pendingHallPasses: TransformedHallPass[];
  pendingReturns: TransformedHallPass[];
  summons: Summons[];
  todaysBirthdays: Student[];
  students: Student[];
  items: Item[];
  onApproveRequest: (request: Request) => void;
  onDenyRequest: (request: Request) => void;
  onApproveCheckIn: (request: CheckInRequest) => void;
  onDenyCheckIn: (request: CheckInRequest) => void;
  onClearAllRequests: () => void;
  onClearAllCheckIns: () => void;
  onNavigateToPage?: () => void;
  onOpenSettings: () => void;
  onApproveHallPass: (id: string) => void;
  onDenyHallPass: (id: string) => void;
  onProcessReturnOnTime: (id: string) => void;
  onLateReturnClick: (id: string) => void;
  onAcceptSummons: (id: string) => void;
  onAcceptAllSummons: () => void;
}

function BellView({
  pendingRequests,
  pendingCheckIns,
  pendingHallPasses,
  pendingReturns,
  summons,
  todaysBirthdays,
  students,
  items,
  onNavigateToPage,
  onOpenSettings,
  onApproveRequest,
  onDenyRequest,
  onApproveCheckIn,
  onDenyCheckIn,
  onApproveHallPass,
  onDenyHallPass,
  onProcessReturnOnTime,
  onLateReturnClick,
  onAcceptSummons,
  onAcceptAllSummons
}: BellViewProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'all'|'equipment'|'birthdays'|'summons'|'hall'|'returns'>('all');
  // track birthdays locally so we can decrement the count
  const [birthdaysState, setBirthdaysState] = useState<Student[]>(todaysBirthdays);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showTestButton, setShowTestButton] = useState(false);

  // unique counts only
  const uniqueHall   = new Set(pendingHallPasses.map(p => p.id)).size;
  const uniqueReturn = new Set(pendingReturns.map(p => p.id)).size;
  const uniqueSum    = new Set(summons.map(s => s.id)).size;
  
  const total =
    pendingRequests.length +
    pendingCheckIns.length +
    uniqueHall +
    uniqueReturn +
    //birthdaysState.length + //dont count birthdays until clever integration
    uniqueSum;

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDoubleClick = () => setShowTestButton(true);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        onDoubleClick={handleDoubleClick}
        className="relative p-2 text-gray-600 hover:text-primary-600 focus:outline-none transition-colors duration-200"
      >
        <Bell className="h-6 w-6" />
        {total > 0 && (
          <span className="absolute top-0 right-0 transform translate-x-1/2 -translate-y-1/2 bg-error-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
            {total}
          </span>
        )}
      </button>
      {showTestButton && (
        <button className="absolute top-full right-0 mt-2 px-3 py-1 bg-slate-800 text-white text-xs rounded-md hover:bg-slate-700">
          Test Notification
        </button>
      )}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-[28rem] bg-white rounded-xl shadow-lg border z-50">
          <div className="p-4">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-xl font-semibold text-gray-900">Notifications</h3>
                <div className="flex gap-2 items-center">
                  {onNavigateToPage && (
                    <button
                      onClick={() => {
                        setIsOpen(false);
                        onNavigateToPage();
                      }}
                      title="Open full page"
                      className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      ↗
                    </button>
                  )}
                  <button
                    onClick={onOpenSettings}
                    className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                    title="Notification Settings"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="flex bg-slate-100 rounded-full p-1 gap-1 w-fit">
                {([

                  { id: 'all', icon: Bell },
                  ...(import.meta.env.VITE_ACORN_EDITION === 'full'
                    ? [{ id: 'equipment', icon: Laptop2 }]
                    : []),
                  //{ id: 'birthdays', icon: Cake },
                  { id: 'summons', icon: UserCog },
                  { id: 'hall', icon: DoorClosed },
                  { id: 'returns', icon: DoorOpen },
                ] as const).map(({ id, icon: Icon }) => (
                  <button
                    key={id}
                    onClick={() => setActiveTab(id as 'all' | 'equipment' | 'birthdays' | 'summons' | 'hall' | 'returns')}
                    className={`p-1.5 rounded-full transition-colors ${activeTab === id
                      ? 'bg-white text-slate-900 shadow-sm'
                      : 'text-slate-600 hover:text-slate-900'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                  </button>
                ))}
              </div>
            </div>
            {/* ---- content list --------------------------------------- */}
            <div className="space-y-6 max-h-[70vh] overflow-y-auto pr-1">
              {/* equipment requests */}
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (activeTab === 'all' || activeTab === 'equipment') && (
                <RequestDropdown
                  title="Equipment Requests"
                  icon={Laptop2}
                  pending={pendingRequests}
                  students={students}
                  items={items}
                  onApprove={onApproveRequest}
                  onDeny={onDenyRequest}
                />
              )}
              {import.meta.env.VITE_ACORN_EDITION === 'full' && (activeTab === 'all' || activeTab === 'equipment') && (
                <CheckInDropdown
                  title="Equipment Returns"
                  icon={ArrowLeftRight}
                  pending={pendingCheckIns}
                  students={students}
                  items={items}
                  onApprove={onApproveCheckIn}
                  onDeny={onDenyCheckIn}
                />
              )}
              {(activeTab === 'all' || activeTab === 'summons') && (
                <SummonsDropdown 
                  summons={summons} 
                  students={students} 
                  onAcceptSummons={onAcceptSummons} 
                  onAcceptAllSummons={onAcceptAllSummons} 
                />
              )}
              {(activeTab === 'all' || activeTab === 'hall') && (
                <HallPassDropdown
                  passRequests={pendingHallPasses}
                  students={students}
                  onApprove={onApproveHallPass}
                  onDeny={onDenyHallPass}
                />
              )}
              {(activeTab === 'all' || activeTab === 'returns') && (
                <>
                  <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                    <div className="flex items-center gap-2">
                      <DoorOpen className="w-5 h-5 text-slate-500" />
                      <span className="font-medium text-slate-900">Hall Pass Returns</span>
                      {pendingReturns.length > 0 && (
                        <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-warning-100 text-warning-800">
                          {pendingReturns.length}
                        </span>
                      )}
                    </div>
                  </div>
                  {pendingReturns.length === 0 ? (
                    <div className="text-center py-4 text-slate-500">
                      No hall pass returns pending
                    </div>
                  ) : (
                    pendingReturns.map((pass) => {
                      const student = students.find((s) => s.userId === pass.studentId);
                      return (
                        <div key={pass.id} className="p-2 flex items-center justify-between hover:bg-slate-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <DoorOpen className="w-4 h-4 text-slate-500" />
                            <div>
                              <span>
                                {student?.name}{' '}
                                {pass.summonedBy
                                  ? `summoned by ${pass.summonedBy}`
                                  : `returned from ${pass.type}`}
                              </span>
                              {pass.duration && (
                                <span className="text-slate-400 text-xs block">Duration: {pass.duration} minutes</span>
                              )}
                              {pass.notes && (
                                <span className="text-slate-400 text-xs block">Note: {pass.notes}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => onProcessReturnOnTime(pass.id)}
                              className="p-1 hover:bg-green-100 rounded"
                              title="On Time"
                            >
                              <Check className="w-5 h-5 text-green-600" />
                            </button>
                            <button
                              onClick={() => onLateReturnClick(pass.id)}
                              className="p-1 hover:bg-red-100 rounded"
                              title="Late"
                            >
                              <X className="w-5 h-5 text-red-600" />
                            </button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </>
              )}
              {(activeTab === 'all' || activeTab === 'birthdays') && false && (
                <BirthdayDropdown
                  birthdays={birthdaysState}
                  onAcknowledge={id => setBirthdaysState(bs => bs.filter(s => s.userId !== id))}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------- */
/*                        Shared small building blocks                       */
/* ------------------------------------------------------------------------- */

interface RequestBlockProps {
  title: string;
  icon: React.ElementType;
  pending: Request[];
  students: Student[];
  items: Item[];
  onApprove: (r: Request) => void;
  onDeny: (r: Request) => void;
}

function RequestBlock({ title, icon: Icon, pending, students, items, onApprove, onDeny }: RequestBlockProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Icon className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-warning-100 text-warning-800">
            {pending.length}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => pending.forEach(onApprove)}
            className="px-3 py-1 text-sm font-medium text-success-700 hover:bg-success-50 rounded-lg"
          >
            Approve All
          </button>
          <button
            onClick={() => pending.forEach(onDeny)}
            className="px-3 py-1 text-sm font-medium text-error-700 hover:bg-error-50 rounded-lg"
          >
            Deny All
          </button>
        </div>
      </div>
      <div className="divide-y divide-gray-200">
        {pending.map((req) => {
          const student = students.find((s) => s.userId === req.studentId);
          const item = items.find((i) => i.id === req.itemId);
          return (
            <div key={req.id} className="p-4 hover:bg-gray-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Icon className="w-5 h-5 text-gray-500" />
                  <div>
                    <p className="font-medium text-gray-900">{student?.name}</p>
                    <p className="text-sm text-gray-500">Requested {item?.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => onApprove(req)}
                    className="px-3 py-1 text-sm font-medium text-success-700 hover:bg-success-50 rounded-lg"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => onDeny(req)}
                    className="px-3 py-1 text-sm font-medium text-error-700 hover:bg-error-50 rounded-lg"
                  >
                    Deny
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* CheckInBlock is structurally identical to RequestBlock, only wording differs */
interface CheckInBlockProps extends Omit<RequestBlockProps, 'title' | 'icon' | 'pending'> {
  title: string;
  icon: React.ElementType;
  pending: CheckInRequest[];
}

function CheckInBlock({ title, icon, pending, students, items, onApprove, onDeny }: CheckInBlockProps) {
  return (
    <RequestBlock
      title={title}
      icon={icon}
      pending={pending as unknown as Request[]}
      students={students}
      items={items}
      onApprove={onApprove as any}
      onDeny={onDeny as any}
    />
  );
}

function BirthdayBlock({ birthdays, onAcknowledge }: { birthdays: Student[]; onAcknowledge: (id: string) => void }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
      <div className="p-4 border-b bg-gray-50 flex items-center gap-2">
        <Cake className="w-5 h-5 text-pink-500" />
        <h3 className="font-semibold text-gray-900">Today's Birthdays</h3>
        {birthdays.length > 0 && (
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-pink-100 text-pink-800">
            {birthdays.length}
          </span>
        )}
      </div>
      <div className="divide-y divide-gray-200" hidden>
        {birthdays.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No birthdays today</div>
        ) : (
          birthdays.map(s => (
            <div
              key={s.userId}
              className="p-4 hover:bg-gray-50 flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <Cake className="w-5 h-5 text-pink-500" />
                <div>
                  <p className="font-medium text-gray-900">{s.name}</p>
                  <p className="text-sm text-gray-500">Grade {s.grade}</p>
                  <p className="text-sm text-gray-500">
                    {s.birthDate
                      ? new Date(s.birthDate).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })
                      : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => onAcknowledge(s.userId)}
                className="p-1 hover:bg-green-100 rounded"
                title="Acknowledge birthday"
              >
                <Check className="w-5 h-5 text-success-600" />
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

interface SummonsBlockProps {
  summons: Summons[];
  students: Student[];
  onAcceptSummons: (id: string) => void;
  onAcceptAllSummons: () => void;
}
function SummonsBlock({ summons, students, onAcceptSummons, onAcceptAllSummons }: SummonsBlockProps) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <div className="flex items-center gap-2">
          <UserCog className="w-5 h-5 text-gray-500" />
          <h3 className="text-lg font-semibold text-gray-900">Student Summons</h3>
          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-error-100 text-error-800">
            {summons.length}
          </span>
        </div>
        {summons.length > 0 && (
          <button
            onClick={onAcceptAllSummons}
            className="px-3 py-1 text-sm font-medium text-success-600 hover:bg-success-50 rounded-lg"
          >
            Accept All
          </button>
        )}
      </div>
      <div className="divide-y divide-gray-200">
        {summons.length === 0 ? (
          <div className="p-4 text-center text-gray-500">No active summons</div>
        ) : (
          summons.map((sum) => {
            const student = students.find((s) => s.userId === sum.studentId);
            return (
              <div key={sum.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    {sum.requestedBy?.avatar ? (
                      <img
                        src={sum.requestedBy.avatar}
                        alt={sum.requestedBy.name}
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    ) : (
                      <UserCog className="w-5 h-5 text-gray-500" />
                    )}
                    <div>
                      <p className="font-medium text-gray-900">{student?.name}</p>
                      {sum.requestedBy?.name && (
                        <p className="text-sm text-gray-500">
                          Summoned by <span className="font-medium text-gray-900">{sum.requestedBy.name}</span>
                        </p>
                      )}
                      {sum.location && (
                        <p className="text-sm text-gray-500">
                          Location: <span className="font-medium text-gray-900">{sum.location}</span>
                        </p>
                      )}
                      {sum.notes && (
                        <p className="text-sm text-gray-500">
                          Notes: <span className="font-medium text-gray-900">{sum.notes}</span>
                        </p>
                      )}
                      <p className="text-xs text-gray-400">
                        {new Date(sum.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => onAcceptSummons(sum.id)} className="p-1 hover:bg-success-50 rounded">
                    <Check className="w-5 h-5 text-success-600" />
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

/* ---------------- small dropdown variants ----------------- */

function RequestDropdown(props: RequestBlockProps) {
  return <RequestBlock {...props} />;
}

function CheckInDropdown(props: CheckInBlockProps) {
  return <CheckInBlock {...props} />;
}

function BirthdayDropdown({ birthdays, onAcknowledge }: { birthdays: Student[]; onAcknowledge: (id: string) => void }) {
  return <BirthdayBlock birthdays={birthdays} onAcknowledge={onAcknowledge} />;
}

interface HallPassDropdownProps {
  passRequests: TransformedHallPass[];
  students: Student[];
  onApprove: (id: string) => void;
  onDeny: (id: string) => void;
}

function HallPassDropdown({ passRequests: passRequests, students, onApprove, onDeny }: HallPassDropdownProps) {
  function approveSingle(id: string) {
    onApprove(id);
  }
  function denySingle(id: string) {
    onDeny(id);
  }

  return (
    <div>
      <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
        <div className="flex items-center space-x-2">
          <DoorClosed className="w-5 h-5 text-slate-500" />
          <span className="font-medium text-slate-900 text-base">Hall Pass Requests</span>
          {passRequests.length > 0 && <span className="badge badge-warning">{passRequests.length}</span>}
        </div>
        {passRequests.length > 0 && (
          <div className="flex space-x-3">
            <button onClick={() => passRequests.forEach((pass) => approveSingle(pass.id))} className="text-sm text-success-600 hover:text-success-800 font-medium px-3 py-1 rounded-lg hover:bg-success-50">
              Approve All
            </button>
            <button onClick={() => passRequests.forEach((pass) => denySingle(pass.id))} className="text-sm text-error-600 hover:text-error-800 font-medium px-3 py-1 rounded-lg hover:bg-error-50">
              Deny All
            </button>
          </div>
        )}
      </div>
      <div className="text-sm text-gray-600 mt-3 space-y-2">
        {passRequests.length === 0 ? (
          <p className="text-center py-2 text-slate-500">No hall pass requests pending</p>
        ) : (
          passRequests.map((pass) => {
            const student = students.find((s) => s.userId === pass.studentId);
            return (
              <div key={pass.id} className="p-2 flex items-center justify-between hover:bg-slate-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  {getHallPassIcon(pass.type as unknown as HallPassType)}
                  <div>
                    <span>{student?.name} requested {pass.type} pass</span>
                    {pass.duration && (
                      <span className="text-slate-400 text-xs block">Duration: {pass.duration} minutes</span>
                    )}
                    {pass.notes && (
                      <span className="text-slate-400 text-xs block">Note: {pass.notes}</span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button onClick={() => approveSingle(pass.id)} className="text-success-600 hover:text-success-800 p-1 hover:bg-success-50 rounded">
                    ✓
                  </button>
                  <button onClick={() => denySingle(pass.id)} className="text-error-600 hover:text-error-800 p-1 hover:bg-error-50 rounded">
                    ✕
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

interface SummonsDropdownProps {
  summons: Summons[];
  students: Student[];
  onAcceptSummons: (id: string) => void;
  onAcceptAllSummons: () => void;
}

function SummonsDropdown({ summons, students, onAcceptSummons, onAcceptAllSummons }: SummonsDropdownProps) {
  return <SummonsBlock summons={summons} students={students} onAcceptSummons={onAcceptSummons} onAcceptAllSummons={onAcceptAllSummons} />;
}